import { loadTwStation, peaksText, trendText } from "./tw-trend";
import { isLight, onThemeIcons, putImage } from "./theme";
import maplibregl, { type Map as MlMap } from "maplibre-gl";
import { openMapPopup } from "./popup-room";

/**
 * ThaiWater (คลังข้อมูลน้ำแห่งชาติ · สสน.) national water-level stations, read from
 * the backend proxy /api/thaiwater (never the upstream directly). Each station is
 * coloured/sized by its level vs the river bank: over (ล้นตลิ่ง, +X ม.) / near
 * (ใกล้ตลิ่ง) / normal / aged. Body-depth wording uses the REAL over-bank metres
 * only (never estimated). SPEC v0.9.1. Credit: ThaiWater · สสน.
 */
export const THAIWATER_CREDIT = "ข้อมูล: คลังข้อมูลน้ำแห่งชาติ (ThaiWater) · สสน.";

interface Station {
  id: unknown; sid?: number | null; name: string; river: string | null; lat: number; lon: number;
  amphoe: string | null; basin: string | null; agency: string | null;
  diffBankM: number | null; level: number | null; status: string; trend: string | null; datetime: string | null;
}
const SRC = "tw-src", LABELS = "tw-labels";
let loaded = false;
let stations: Station[] = [];

function isStale(dt: string | null): boolean {
  if (!dt) return true;
  const t = Date.parse(dt.replace(" ", "T") + "+07:00");
  return !Number.isFinite(t) || Date.now() - t > 3 * 3600 * 1000; // >3h (SPEC A3)
}
/** Over-bank height worded as a RIVER level (never "น้ำท่วม" — SPEC v0.9.1c §4). */
function depthWord(m: number): string {
  return m < 0.5 ? "น้ำในลำน้ำเริ่มล้นตลิ่ง" : m < 1.5 ? "น้ำในลำน้ำสูงกว่าตลิ่งพอควร" : "น้ำในลำน้ำสูงกว่าตลิ่งมาก";
}

/* ── water-level "staff" icons (owner-approved SVG → canvas → addImage) ──────
 * A ruler staff with a water fill + ticks + a dashed bank line + waves at the base.
 * Deliberately NOT a circle (POPNIX) or a tank gauge (dam). SPEC v0.9.1c. */
const STAFF_IMG: Record<string, string> = {
  normal: "twi-normal", near: "twi-near", over1: "twi-over1", over2: "twi-over2", over3: "twi-over3", nobank: "twi-nobank",
};
const STAFF: Record<string, { stroke: string; fill: string; wy: number; wh: number; outline?: string }> = {
  normal: { stroke: "#9aa4b2", fill: "#5DCAA5", wy: 66, wh: 16 },
  near: { stroke: "#EF9F27", fill: "#FAC775", wy: 30, wh: 52 },
  over1: { stroke: "#D85A30", fill: "#F0997B", wy: 22, wh: 60 },
  over2: { stroke: "#E24B4A", fill: "#E24B4A", wy: 12, wh: 70 },
  over3: { stroke: "#7F77DD", fill: "#7F77DD", wy: 2, wh: 80, outline: "#AFA9EC" },
};
const VB_X = -14, VB_Y = -6, VB_W = 46, VB_H = 100, STAFF_S = 2;
function rr(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath(); ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r); ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
}
function drawStaff(ctx: CanvasRenderingContext2D, key: string): void {
  if (key === "nobank") {
    ctx.strokeStyle = "#888780"; ctx.lineWidth = 2; ctx.setLineDash([4, 3]);
    rr(ctx, 0, 0, 18, 84, 3); ctx.stroke(); ctx.setLineDash([]);
    ctx.fillStyle = "#B4B2A9"; ctx.font = "bold 22px sans-serif"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText("?", 9, 44); return;
  }
  const st = STAFF[key]!;
  const light = isLight(); // v0.9.9 B5: light variant = white staff body, dark ticks (same shape / colours)
  rr(ctx, 0, 0, 18, 84, 3); ctx.fillStyle = light ? "#ffffff" : "#1b2433"; ctx.fill();          // staff body
  ctx.fillStyle = st.fill; ctx.fillRect(2, st.wy, 14, st.wh);               // water
  rr(ctx, 0, 0, 18, 84, 3); ctx.strokeStyle = st.stroke; ctx.lineWidth = 2; ctx.stroke(); // outline
  ctx.strokeStyle = light ? "#334155" : "#e6edf5"; ctx.lineWidth = 1.5; ctx.beginPath();        // ticks
  for (const [ly, len] of [[14, 7], [28, 5], [42, 7], [56, 5], [70, 7]] as const) { ctx.moveTo(18, ly); ctx.lineTo(18 - len, ly); }
  ctx.stroke();
  ctx.setLineDash([3, 3]); ctx.beginPath(); ctx.moveTo(-6, 30); ctx.lineTo(24, 30); ctx.stroke(); ctx.setLineDash([]); // bank line
  ctx.strokeStyle = st.fill; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(-12, 88);   // waves
  let x = -12, up = true;
  for (let i = 0; i < 4; i++) { ctx.quadraticCurveTo(x + 5, up ? 83 : 93, x + 10, 88); x += 10; up = !up; }
  ctx.stroke();
  if (st.outline) { rr(ctx, -4, -4, 26, 92, 6); ctx.strokeStyle = st.outline; ctx.lineWidth = 1.5; ctx.stroke(); }
}
function addStaffImage(map: MlMap, id: string, key: string): void {
  const c = document.createElement("canvas"); c.width = VB_W * STAFF_S; c.height = VB_H * STAFF_S;
  const ctx = c.getContext("2d"); if (!ctx) return;
  ctx.scale(STAFF_S, STAFF_S); ctx.translate(-VB_X, -VB_Y); ctx.lineJoin = "round"; ctx.lineCap = "round";
  drawStaff(ctx, key);
  putImage(map, id, ctx.getImageData(0, 0, VB_W * STAFF_S, VB_H * STAFF_S), { pixelRatio: STAFF_S });
}
/** icon state from status + over-bank metres. */
function iconKey(status: string, diff: number | null): string {
  if (status === "nobank" || status === "suspect") return "nobank";
  if (status === "normal") return "normal";
  if (status === "near") return "near";
  if (status === "over") { const d = diff ?? 0; return d < 0.5 ? "over1" : d <= 1.5 ? "over2" : "over3"; }
  return "normal";
}
const haversine = (aLat: number, aLon: number, bLat: number, bLon: number): number => {
  const R = 6371, t = (x: number) => (x * Math.PI) / 180;
  const dLat = t(bLat - aLat), dLon = t(bLon - aLon);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(t(aLat)) * Math.cos(t(bLat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};

function fc(): GeoJSON.FeatureCollection {
  return { type: "FeatureCollection", features: stations.map((s, i) => {
    const key = iconKey(s.status, s.diffBankM);
    return {
      type: "Feature", geometry: { type: "Point", coordinates: [s.lon, s.lat] },
      properties: {
        i, // index into `stations` — the card lookup (tile coordinates are rounded, never equal to the source)
        status: s.status, stale: isStale(s.datetime),
        diff: s.diffBankM ?? 0, icon: STAFF_IMG[key], over3: key === "over3",
        label: s.status === "over" && s.diffBankM != null ? `+${s.diffBankM.toFixed(1)}` : "",
      },
    };
  }) };
}

/** Load station data (for the card's nearest-station lookup) without touching the
 *  map — so the card works even when the layer toggle is off. */
export async function loadThaiWaterData(): Promise<void> {
  if (stations.length) return;
  try {
    const res = await fetch("/api/thaiwater");
    const data = (await res.json()) as { stations?: Station[] };
    stations = data.stations ?? [];
  } catch { /* card simply omits the ThaiWater line */ }
}

const OVER = "tw-over", NEAR = "tw-near", NORMAL = "tw-normal", PULSE = "tw-pulse";
const DOT_LAYERS = [PULSE, OVER, NEAR, NORMAL];
let pulseTimer: ReturnType<typeof setInterval> | undefined;
/** Gentle "ขอบกะพริบ" for >1.5 m staffs — a slow opacity/radius pulse (no rAF). */
function startPulse(map: MlMap): void {
  if (pulseTimer) return;
  let t = 0;
  pulseTimer = setInterval(() => {
    if (!map.getLayer(PULSE)) return;
    const vis = map.getLayoutProperty(PULSE, "visibility");
    if (vis === "none") return;
    t += 0.5;
    const o = 0.18 + 0.18 * (Math.sin(t) + 1) / 2;
    try { map.setPaintProperty(PULSE, "circle-opacity", o); map.setPaintProperty(PULSE, "circle-radius", 10 + 4 * (Math.sin(t) + 1) / 2); } catch { /* layer gone */ }
  }, 450);
}
export function stopThaiWaterPulse(): void { if (pulseTimer) { clearInterval(pulseTimer); pulseTimer = undefined; } }

/** Add the station layers ONCE, visible by default: over-bank (ล้นตลิ่ง) at ALL
 *  zooms nationwide (it's the safety signal), near at zoom ≥7, normal at zoom ≥9.
 *  Called on map load — no point selection / radius needed (SPEC v0.9.1a §1). */
export async function initThaiWaterLayer(map: MlMap): Promise<void> {
  if (loaded) return;
  await loadThaiWaterData();
  if (map.getSource(SRC)) { loaded = true; return; }
  const staffs = (): void => { for (const [key, id] of Object.entries(STAFF_IMG)) addStaffImage(map, id, key); };
  staffs(); onThemeIcons(staffs);
  map.addSource(SRC, { type: "geojson", data: fc() });

  // Staff size by zoom (small far out: ~14px tall at z5–6, full at z11). §2
  // v0.9.4a A7: on a phone the z5–6 staffs were still too tall → ~30% smaller there.
  const phone = window.matchMedia("(max-width: 767px)").matches;
  const staffSize: maplibregl.ExpressionSpecification = phone
    ? ["interpolate", ["linear"], ["zoom"], 5, 0.245, 6, 0.30, 8, 0.6, 11, 1.0]
    : ["interpolate", ["linear"], ["zoom"], 5, 0.35, 8, 0.6, 11, 1.0];
  // Insert ThaiWater BELOW the dam layers so dam icons + the C.13 "ระบาย" label are
  // never covered by a staff (§3). If dams aren't added yet, they'll stack on top later.
  const below = map.getLayer("dams-layer") ? "dams-layer" : undefined;
  // soft pulsing halo behind >1.5 m (over3) staffs
  map.addLayer({ id: PULSE, type: "circle", source: SRC, minzoom: 0, filter: ["get", "over3"], paint: {
    "circle-radius": 11, "circle-color": "#AFA9EC", "circle-opacity": 0.28, "circle-stroke-width": 0, "circle-translate": [0, -14] } }, below);
  // over-bank — staff at ALL zooms. At zoom <8 let MapLibre CULL colliding staffs
  // (allow-overlap false) so only the highest-value one survives (symbol-sort-key);
  // from zoom ≥8 show them all. §2/§7
  map.addLayer({ id: OVER, type: "symbol", source: SRC, minzoom: 0, filter: ["==", ["get", "status"], "over"],
    layout: { "icon-image": ["get", "icon"], "icon-size": staffSize, "icon-anchor": "bottom",
      "icon-allow-overlap": ["step", ["zoom"], false, 8, true], "icon-ignore-placement": false, // v0.9.5c: staffs block river names
      "symbol-sort-key": ["-", 0, ["get", "diff"]] },
    paint: { "icon-opacity": ["case", ["get", "stale"], 0.5, 1] } }, below);
  // near — zoom ≥7
  map.addLayer({ id: NEAR, type: "symbol", source: SRC, minzoom: 7, filter: ["==", ["get", "status"], "near"],
    layout: { "icon-image": ["get", "icon"], "icon-size": staffSize, "icon-anchor": "bottom", "icon-allow-overlap": false },
    paint: { "icon-opacity": ["case", ["get", "stale"], 0.5, 0.95] } }, below);
  // normal / no-bank / suspect — zoom ≥9 (staff shows the state; nobank = dashed "?")
  map.addLayer({ id: NORMAL, type: "symbol", source: SRC, minzoom: 9, filter: ["in", ["get", "status"], ["literal", ["normal", "nobank", "suspect"]]],
    layout: { "icon-image": ["get", "icon"], "icon-size": staffSize, "icon-anchor": "bottom", "icon-allow-overlap": false },
    paint: { "icon-opacity": ["case", ["get", "stale"], 0.4, 0.8] } }, below);
  // "+X.X ม." label ABOVE the staff head (text-anchor bottom + offset up), strong halo,
  // only from zoom ≥8. §4
  map.addLayer({ id: LABELS, type: "symbol", source: SRC, minzoom: 8, filter: ["==", ["get", "status"], "over"],
    layout: { "text-field": ["concat", ["get", "label"], " ม."], "text-font": ["Noto Sans Regular"],
      "text-size": 12, "text-anchor": "bottom", "text-offset": [0, -6], "text-allow-overlap": false, "symbol-sort-key": ["-", 0, ["get", "diff"]] },
    paint: { "text-color": "#ffffff", "text-halo-color": "#05080f", "text-halo-width": 2.4, "text-opacity": ["case", ["get", "stale"], 0.78, 1] } }, below);
  startPulse(map);

  const onClick = (e: maplibregl.MapLayerMouseEvent): void => {
    const f = e.features?.[0]; if (!f) return;
    // v0.9.10: by index — the old exact-coordinate match never hit (rendered coordinates are tile-rounded),
    // so a tapped station never opened its card
    const s = stations[Number(f.properties?.i)];
    if (s) { const pop = openMapPopup(map, [s.lon, s.lat], stationCard(s), "260px"); void fillTrend(pop.getElement(), s); }
  };
  for (const id of DOT_LAYERS) {
    map.on("click", id, onClick);
    map.on("mouseenter", id, () => { map.getCanvas().style.cursor = "pointer"; });
    map.on("mouseleave", id, () => { map.getCanvas().style.cursor = ""; });
  }
  loaded = true;
}

const esc = (s: string): string => s.replace(/[<>&"]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" }[c] ?? c));

function levelText(s: Station): string {
  const stale = isStale(s.datetime);
  if (s.status === "nobank") return `<span class="muted">ไม่มีค่าระดับตลิ่งที่ใช้ได้ — จึงไม่แสดงระดับเหนือตลิ่ง</span>`;
  if (s.status === "suspect") return `<span class="muted">ค่าระดับเกินเกณฑ์สมเหตุสมผล — ถือว่าน่าสงสัย ไม่แสดงตัวเลข</span>`;
  if (s.diffBankM == null) return `<span class="muted">ไม่มีข้อมูลระดับน้ำ</span>`;
  if (s.status === "over") return `<b style="color:#ff6b8a">📏 น้ำในแม่น้ำสูงกว่าตลิ่ง ${s.diffBankM.toFixed(2)} ม.</b><div class="muted small">${depthWord(s.diffBankM)}${stale ? " · ข้อมูลเก่า" : ""}</div>`;
  if (s.status === "near") return s.diffBankM >= 0
    ? `<b style="color:#ffb020">เสมอตลิ่ง / ใกล้ล้น</b> <span class="muted small">(${s.diffBankM.toFixed(2)} ม.)</span>`
    : `<b style="color:#ffb020">ใกล้ตลิ่ง</b> <span class="muted small">(ต่ำกว่าตลิ่ง ${Math.abs(s.diffBankM).toFixed(2)} ม.)</span>`;
  return `<span style="color:#6fd0ff">ปกติ</span> <span class="muted small">(ต่ำกว่าตลิ่ง ${Math.abs(s.diffBankM).toFixed(2)} ม.)</span>`;
}
/** v0.9.10 A: the 6-h measured trend (+ today's / yesterday's highest reading in the tidal reach) */
async function fillTrend(root: HTMLElement | undefined, s: Station): Promise<void> {
  const host = root?.querySelector<HTMLElement>(".tw-trend"); if (!host) return;
  const d = s.sid ? await loadTwStation(s.sid) : null;
  if (!host.isConnected) return;
  const peaks = d?.tidal ? peaksText(d.peaks) : null;
  host.innerHTML = `<b>${esc(trendText(d?.available ? d.trend6 : null))}</b> <span class="muted small">(6 ชม.ล่าสุด · วัดจริง)</span>`
    + (d?.tidal ? `<div class="tw-peaks small">🌊 ในระยะน้ำทะเลหนุน (แม่น้ำ${esc(d.tidal.river)}) · ${esc(peaks ?? "ยังไม่มีข้อมูลยอดน้ำขึ้น (วัดจริง)")}</div>` : "");
}
export function stationCard(s: Station): string {
  const trend = "";
  const when = s.datetime ? esc(s.datetime) + " น." : "—";
  return `<div class="pop ${isStale(s.datetime) ? "stale" : ""}">
    <div class="pop-row"><b>สถานี ${esc(s.name)}</b>${s.river ? ` <span class="muted small">${esc(s.river)}</span>` : ""}</div>
    ${levelText(s)}
    <div class="tw-trend small" style="margin-top:4px">กำลังโหลดแนวโน้ม…</div>
    <div class="muted small" style="margin-top:4px">${trend ? trend + " · " : ""}${s.amphoe ? "อ." + esc(s.amphoe) + " · " : ""}${s.agency ? esc(s.agency) : ""}</div>
    <div class="muted small" style="margin-top:4px">📏 ระดับน้ำในลำน้ำ ไม่ใช่ความลึกน้ำท่วมบนถนน/บ้าน</div>
    <div class="muted small" style="margin-top:6px">ที่มา: สสน. (ThaiWater) · ${when}${isStale(s.datetime) ? " (อาจไม่เป็นปัจจุบัน)" : ""}</div>
  </div>`;
}

export async function setThaiWaterVisible(map: MlMap, on: boolean): Promise<void> {
  await initThaiWaterLayer(map);
  for (const id of [...DOT_LAYERS, LABELS]) if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", on ? "visible" : "none");
}

/** Nearest over/near station within radius (prefers over-bank, then distance). */
export function nearestThaiWater(lat: number, lon: number, radiusKm: number): (Station & { distKm: number }) | null {
  let best: (Station & { distKm: number }) | null = null;
  for (const s of stations) {
    if (s.status !== "over" && s.status !== "near") continue;
    const distKm = haversine(lat, lon, s.lat, s.lon);
    if (distKm > radiusKm) continue;
    const score = (x: Station) => (x.status === "over" ? 0 : 1);
    if (!best || score(s) < score(best) || (score(s) === score(best) && distKm < best.distKm)) best = { ...s, distKm };
  }
  return best;
}
export function thaiWaterLoaded(): boolean { return loaded; }

export interface TWStation { name: string; river: string | null; lat: number; lon: number; amphoe: string | null; agency: string | null; diffBankM: number | null; datetime: string | null; distKm: number | null }
/** Over-bank stations ranked by height above bank (m), for the "สูงสุด" tab.
 *  Separate from POPNIX (cm on road) — units must never be merged (SPEC §4). */
export function rankThaiWater(center: { lat: number; lon: number } | null, radiusKm: number | null): { fresh: TWStation[]; staleCount: number } {
  const fresh: TWStation[] = []; let staleCount = 0;
  for (const s of stations) {
    if (s.status !== "over" || s.diffBankM == null) continue;
    const distKm = center ? haversine(center.lat, center.lon, s.lat, s.lon) : null;
    if (radiusKm != null && distKm != null && distKm > radiusKm) continue;
    if (isStale(s.datetime)) { staleCount++; continue; }
    fresh.push({ name: s.name, river: s.river, lat: s.lat, lon: s.lon, amphoe: s.amphoe, agency: s.agency, diffBankM: s.diffBankM, datetime: s.datetime, distKm });
  }
  fresh.sort((a, b) => (b.diffBankM ?? 0) - (a.diffBankM ?? 0));
  return { fresh, staleCount };
}
export async function ensureThaiWaterData(): Promise<void> { await loadThaiWaterData(); }

/** v0.9.5 สรุป — every FRESH station (≤3 h old) within the radius, nearest first, with
 *  its real status. Stale readings are left out (they must not raise or lower a level). */
export function stationsInRadius(lat: number, lon: number, radiusKm: number): { sid: number | null; name: string; river: string | null; status: string; diffBankM: number | null; distKm: number; datetime: string | null; lat: number; lon: number }[] {
  const out: { sid: number | null; name: string; river: string | null; status: string; diffBankM: number | null; distKm: number; datetime: string | null; lat: number; lon: number }[] = [];
  for (const s of stations) {
    if (isStale(s.datetime)) continue;
    const distKm = haversine(lat, lon, s.lat, s.lon);
    if (distKm <= radiusKm) out.push({ sid: s.sid ?? null, name: s.name, river: s.river, status: s.status, diffBankM: s.diffBankM, distKm, datetime: s.datetime, lat: s.lat, lon: s.lon });
  }
  return out.sort((a, b) => a.distKm - b.distKm);
}
